import React from 'react';
import { PRODUCT_MOCKUP, PRODUCT_MOCKUP_ALT } from '../data/product';

/** Small stick-figure gymnast in a star shape (flat line style). */
function StarGymnast({
  x,
  y,
  s = 1,
  color = '#16213E'
}: {
  x: number;
  y: number;
  s?: number;
  color?: string;
}) {
  return <g transform={`translate(${x} ${y}) scale(${s})`} stroke={color} strokeWidth="3.2" strokeLinecap="round" fill="none" data-id="element-254" data-mp="3shx9e66vg514">
      <circle cx="0" cy="-26" r="6" fill={color} stroke="none" data-id="element-255" data-mp="o1mg4tru3gq2" />
      <path d="M0 -18 L0 4" data-id="element-256" data-mp="o3fme7yjbyxj" />
      <path d="M0 -14 L-18 -26 M0 -14 L18 -26" data-id="element-257" data-mp="t0604omvaifs" />
      <path d="M0 4 L-14 24 M0 4 L14 24" data-id="element-258" data-mp="szxpl3e7vp7r" />
    </g>;
}

/**
 * Shows the uploaded mockup when PRODUCT_MOCKUP is set; otherwise draws the
 * cover in code: Cloud page, apparatus colour strip, cartwheel trajectory.
 */
export function ProductVisual({
  className = '',
  lazy = false
}: {
  className?: string;
  lazy?: boolean;
}) {
  if (PRODUCT_MOCKUP) {
    return <img src={PRODUCT_MOCKUP} alt={PRODUCT_MOCKUP_ALT} loading={lazy ? 'lazy' : undefined} className={`w-full rounded-3xl shadow-book ${className}`} data-id="element-259" data-mp="1brgmqtlk891v" />;
  }
  return <div className={`relative mx-auto ${className}`} role="img" aria-label={PRODUCT_MOCKUP_ALT} data-id="element-260" data-mp="1ze1b356p3teg">
      {/* the other volumes, fanned behind */}
      <div className="absolute inset-0 translate-x-[9%] translate-y-[2%] rotate-[6deg] rounded-[14px] bg-amber shadow-book" aria-hidden="true" data-id="element-261" data-mp="2e48upo8fvigq" />
      <div className="absolute inset-0 translate-x-[6%] translate-y-[1.5%] rotate-[4deg] rounded-[14px] bg-teal shadow-book" aria-hidden="true" data-id="element-262" data-mp="2n22oaretjy07" />
      <div className="absolute inset-0 translate-x-[3%] translate-y-[1%] rotate-[2deg] rounded-[14px] bg-indigo shadow-book" aria-hidden="true" data-id="element-263" data-mp="2j0z5bw1h5lur" />

      <div style={{
      containerType: 'inline-size'
    }} className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-white shadow-book" data-id="element-264" data-mp="2s1fbxedil93u">
        <span className="apparatus-strip absolute inset-x-0 top-0 h-[3.2%]" aria-hidden="true" data-id="element-265" data-mp="12otxqkg8cqlm" />
        <div className="relative px-[7%] pt-[8%]" data-id="element-266" data-mp="xq0fkeq00bf4">
          <p className="font-display text-[3cqw] font-bold uppercase tracking-[0.2em] text-coral-deep" data-id="element-267" data-mp="2xr7449ydr3pj">
            A full year · 52 weekly classes
          </p>
          <p className="mt-[2cqw] font-display text-[8.6cqw] font-extrabold leading-[0.95] tracking-tight text-ink" data-id="element-268" data-mp="36qtvs41b4v5e">
            Rec Gymnastics
            <br data-id="element-269" data-mp="3bqpz0126kijw" />
            Class-Ready
          </p>
          <p className="mt-[2.4cqw] max-w-[62%] font-sans text-[3.1cqw] font-medium leading-snug text-slate" data-id="element-270" data-mp="3gp1829knip12">
            Done-for-You Lesson Plans &amp; Skill Progressions
          </p>
        </div>

        {/* floor lane + cartwheel trajectory */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-271" data-mp="3qg4cnlcshm53">
          <rect x="0" y="238" width="400" height="62" fill="#F4F6FB" data-id="element-272" data-mp="3uf4rytw266nc" />
          <line x1="24" y1="258" x2="376" y2="258" stroke="#FF5A5F" strokeWidth="3" strokeDasharray="2 9" strokeLinecap="round" data-id="element-273" data-mp="3awi3l3xt52b" />
          <path d="M196 250 C 240 150, 330 150, 362 250" stroke="#2541B2" strokeWidth="2.4" strokeDasharray="6 7" fill="none" data-id="element-274" data-mp="89pwrguyn242" />
          <circle cx="262" cy="258" r="5" fill="#FF5A5F" data-id="element-275" data-mp="ias8awg7tzxs" />
          <circle cx="290" cy="258" r="5" fill="#FF5A5F" data-id="element-276" data-mp="dda93xs02z2z" />
          <circle cx="318" cy="258" r="5" fill="#17B8A6" data-id="element-277" data-mp="8ep7pv10i9cy" />
          <circle cx="346" cy="258" r="5" fill="#17B8A6" data-id="element-278" data-mp="8f8qtf2ef3sk" />
          <StarGymnast x={300} y={196} s={1.25} data-id="element-279" data-mp="4e5bcto9j9rv" />
          {/* level dots */}
          <g transform="translate(28 222)" data-id="element-280" data-mp="113cyg64j5qxw">
            <circle cx="0" cy="0" r="5" fill="#17B8A6" data-id="element-281" data-mp="112bch6lz7k1e" />
            <circle cx="16" cy="0" r="5" fill="#FFC53D" data-id="element-282" data-mp="w4tci5pxsf5j" />
            <circle cx="32" cy="0" r="5" fill="#FF5A5F" data-id="element-283" data-mp="w5bezk36zplw" />
          </g>
          <text x="28" y="282" fill="#5A6072" fontFamily="Montserrat, sans-serif" fontWeight="700" fontSize="11" letterSpacing="2" data-id="element-284" data-mp="rcrrnvepdg3w">
            FLOOR · BARS · BEAM · VAULT
          </text>
        </svg>
      </div>
    </div>;
}